import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { CalendarDays, UploadCloud, Download, Trash2, FileText, X } from "lucide-react";
import { toast } from "sonner";

export default function SchoolCalendarPdfPanel({ can }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const editable = can("manage_calendar");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/school-calendar");
      setData(data);
    } catch {/* ignore */}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const removePdf = async () => {
    if (!window.confirm("Remove the current school calendar PDF? Parents will no longer be able to download it.")) return;
    try {
      await adminApi.delete("/admin/school-calendar/pdf");
      toast.success("School calendar removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const pdf = data?.pdf;

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="school-calendar-pdf-panel">
      <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#5BC4B8]/20 text-[#0F1B47] flex items-center justify-center">
            <CalendarDays size={18} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">School calendar (PDF)</h2>
            <p className="text-sm text-[#4A5568] mt-0.5 max-w-2xl">Upload the annual school calendar as a single PDF. Parents can download it from their portal.</p>
          </div>
        </div>
        {editable && (
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
            data-testid="school-calendar-upload-btn"
          >
            <UploadCloud size={14} /> {pdf ? "Replace PDF" : "Upload PDF"}
          </button>
        )}
      </div>

      <div className="p-6 md:p-7">
        {loading ? (
          <div className="text-sm text-[#718096] py-6 text-center">Loading…</div>
        ) : !pdf ? (
          <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
            <CalendarDays size={28} className="mx-auto text-[#5BC4B8] mb-2" />
            <div className="text-[#0F1B47] font-medium">No school calendar uploaded yet</div>
            <p className="text-xs text-[#718096] mt-1">{editable ? 'Click "Upload PDF" to publish the annual school calendar.' : "The school calendar will appear here once an admin uploads it."}</p>
          </div>
        ) : (
          <div className="bg-[#F5F5F0] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="school-calendar-current">
            <div className="w-12 h-12 rounded-xl bg-[#FDFDF9] text-[#5BC4B8] flex items-center justify-center flex-shrink-0">
              <FileText size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#0F1B47] truncate">
                {data?.title || pdf.filename}
              </div>
              <div className="text-xs text-[#4A5568] mt-0.5">
                {data?.academic_year ? `Academic year ${data.academic_year} · ` : ""}
                Uploaded {new Date(pdf.uploaded_at).toLocaleString()} · {fmtSize(pdf.size)}
              </div>
              {data?.note && <div className="text-[11px] text-[#718096] italic mt-1">"{data.note}"</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/school-calendar.pdf`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1B47] px-3.5 py-2 rounded-full bg-[#E3EDF9] hover:bg-[#CFE0F0]"
                data-testid="school-calendar-download"
              >
                <Download size={12} /> PDF
              </a>
              {editable && (
                <button onClick={removePdf} className="text-red-600 hover:text-red-700 p-2" title="Remove" data-testid="school-calendar-delete">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {showUpload && (
        <UploadModal
          existing={data}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); load(); }}
        />
      )}
    </div>
  );
}

function UploadModal({ existing, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(existing?.title || "");
  const [academicYear, setAcademicYear] = useState(existing?.academic_year || "");
  const [note, setNote] = useState(existing?.note || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a PDF to upload");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("academic_year", academicYear);
      fd.append("note", note);
      await adminApi.post("/admin/school-calendar/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("School calendar published.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-[28px] w-full max-w-md shadow-2xl overflow-hidden" data-testid="school-calendar-upload-modal">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium">Upload school calendar</h3>
            <p className="text-xs text-[#4A5568] mt-0.5">PDF only · max 15 MB</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Title (optional)">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Annual School Calendar" className={inputCls} data-testid="school-calendar-title" />
          </Field>
          <Field label="Academic year (optional)">
            <input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="e.g. 2026-27" className={inputCls} data-testid="school-calendar-year" />
          </Field>
          <Field label="Note for parents (optional)">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Dates subject to change" className={inputCls} data-testid="school-calendar-note" />
          </Field>
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">PDF file *</span>
            <div
              onClick={() => fileRef.current?.click()}
              className="cursor-pointer rounded-2xl border-2 border-dashed border-[#0F1B47]/15 bg-[#F5F5F0] hover:bg-[#0F1B47]/5 px-5 py-8 text-center transition-colors"
              data-testid="school-calendar-dropzone"
            >
              <UploadCloud size={22} className="mx-auto text-[#5BC4B8]" />
              <div className="text-sm font-semibold text-[#0F1B47] mt-2">{file ? file.name : "Click to choose a PDF"}</div>
              <div className="text-[11px] text-[#718096] mt-0.5">{file ? fmtSize(file.size) : "or drag & drop"}</div>
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="school-calendar-file" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3">
          <button type="submit" disabled={busy || !file} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="school-calendar-upload-confirm">
            {busy ? "Uploading…" : "Publish calendar"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9] transition-colors";

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
